import classNames from 'classnames'
import { useState } from 'react'

import { t } from '../../../../../../common/i18n.js'
import { Language } from '../../../../../../common/types/i18n.js'
import { formatDisplayDate } from '../../../../../../common/utils/format-utils.js'
import {
    formatIban,
    isValidIban,
    isValidSwift,
    normalizeIban,
    normalizeSwift,
} from '../../../../../../common/utils/iban.js'
import { Button } from '../../../../../components/button/button.js'
import { CopyButton } from '../../../../../components/copy-button/copy-button.js'
import { Select } from '../../../../../components/forms/select/select.js'
import { TextField } from '../../../../../components/forms/text-field/text-field.js'
import { Coins, Eye, EyeOff, Plus } from '../../../../../components/icon/solar-icons.js'
import {
    PortalTimeline,
    TimelineEntry,
} from '../../../../../components/portal-timeline/portal-timeline.js'
import { FieldRow, Section, SectionHeader } from '../../../../../components/section/section.js'
import { formatNumber } from '../../../utils/format.js'
import type {
    CompensationData,
    CompensationLineItemGroup,
    CompensationLineItemInput,
    CompensationType,
    EmployeeProfileData,
    RequestConfirmFn,
} from '../employee-profile-types.js'
import '../employee-profile-view.css'
import { CompensationLineItemsSection } from './compensation-line-items-section.js'

interface CompensationSectionProps {
    employee: EmployeeProfileData
    compensationData: CompensationData | null
    compensationLineItems: CompensationLineItemGroup[] | null
    // Compensation figures are private-tier: self/admin only.
    showCompensation: boolean
    // Bank details (moved here from Isiklikud andmed, TASK-438) — private-tier.
    canViewBank: boolean
    canEditBank: boolean
    lang: Language
    onCreateCompensation?: (data: {
        gross_amount: number
        currency: string
        pay_period: string
        fte: number
        effective_from: string
        note: string
    }) => Promise<boolean>
    // In-place correction of an existing compensation row — all fields, incl.
    // both effective dates (the no-overlap guard rejects colliding periods).
    onUpdateCompensation?: (
        compensationId: number,
        data: {
            gross_amount: number
            currency: string
            pay_period: string
            fte: number
            effective_from: string
            effective_to: string | null
            note: string
        },
    ) => Promise<boolean>
    onDeleteCompensation?: (compensationId: number) => Promise<boolean>
    // Additional-compensation (bonus / allowance) management — admin-gated.
    compensationTypes?: CompensationType[]
    onCreateLineItem?: (data: CompensationLineItemInput) => Promise<boolean>
    onUpdateLineItem?: (itemId: number, data: CompensationLineItemInput) => Promise<boolean>
    onDeleteLineItem?: (itemId: number) => Promise<boolean>
    onRevealCompensation?: () => Promise<void>
    // Saves the bank fields (same PATCH as the personal-info fields).
    onUpdatePersonal?: (data: Record<string, string>) => Promise<boolean>
    requestConfirm: RequestConfirmFn
}

const BANK_FIELDS = [
    { key: 'bank_name', maxLength: 128 },
    { key: 'bank_account_holder', maxLength: 200 },
    { key: 'iban', maxLength: 50 },
    { key: 'swift', maxLength: 50 },
] as const

/**
 * "Tasustamine ja boonused" — financial & payment data (TASK-438): salaries
 * with history, additional compensation with history, and the bank details
 * block (moved here from Isiklikud andmed). The section-header "Muuda" edits
 * the bank fields; salary/bonus records keep their own timeline actions.
 */
export const CompensationSection = ({
    employee,
    compensationData,
    compensationLineItems,
    showCompensation,
    canViewBank,
    canEditBank,
    lang,
    onCreateCompensation,
    onUpdateCompensation,
    onDeleteCompensation,
    compensationTypes,
    onCreateLineItem,
    onUpdateLineItem,
    onDeleteLineItem,
    onRevealCompensation,
    onUpdatePersonal,
    requestConfirm,
}: CompensationSectionProps) => {
    const ph = t.portal

    // Salary figures are masked (blurred) by default; revealing fires an audit log.
    const [salaryRevealed, setSalaryRevealed] = useState(false)

    // Inline compensation form state. `editingCompId` null = create-new,
    // a number = in-place edit of that record (effective_to is editable then).
    const [showCompForm, setShowCompForm] = useState(false)
    const [editingCompId, setEditingCompId] = useState<number | null>(null)
    const [compGross, setCompGross] = useState('')
    const [compCurrency, setCompCurrency] = useState('EUR')
    const [compPeriod, setCompPeriod] = useState('monthly')
    const [compFte, setCompFte] = useState('1')
    const [compEffective, setCompEffective] = useState(new Date().toISOString().slice(0, 10))
    const [compEffectiveTo, setCompEffectiveTo] = useState('')
    const [compNote, setCompNote] = useState('')
    const [compSubmitting, setCompSubmitting] = useState(false)

    // Open the inline form for a fresh entry.
    const openCompCreate = () => {
        setEditingCompId(null)
        setCompGross('')
        setCompCurrency('EUR')
        setCompPeriod('monthly')
        setCompFte('1')
        setCompEffective(new Date().toISOString().slice(0, 10))
        setCompEffectiveTo('')
        setCompNote('')
        setShowCompForm(true)
    }

    // Open the inline form pre-filled to correct an existing entry.
    const openCompEdit = (rec: CompensationData['history'][number]) => {
        setEditingCompId(rec.id)
        setCompGross(String(rec.grossAmount))
        setCompCurrency(rec.currency)
        setCompPeriod(rec.payPeriod)
        setCompFte(String(rec.fte))
        setCompEffective(rec.effectiveFrom)
        setCompEffectiveTo(rec.effectiveTo ?? '')
        setCompNote(rec.note ?? '')
        setShowCompForm(true)
    }

    const closeCompForm = () => {
        setShowCompForm(false)
        setEditingCompId(null)
    }

    // Bank details edit mode — driven by the section-header "Muuda".
    const [isEditingBank, setIsEditingBank] = useState(false)
    const [bankDraft, setBankDraft] = useState<Record<string, string>>({})
    const [bankSaving, setBankSaving] = useState(false)

    const bankLabels: Record<(typeof BANK_FIELDS)[number]['key'], string> = {
        bank_name: ph.profile.bankName(lang),
        bank_account_holder: ph.profile.bankAccountHolder(lang),
        iban: ph.profile.iban(lang),
        swift: ph.profile.swift(lang),
    }

    const openBankEdit = () => {
        setBankDraft({
            bank_name: employee.bank_name ?? '',
            bank_account_holder: employee.bank_account_holder ?? '',
            iban: employee.iban ?? '',
            swift: employee.swift ?? '',
        })
        setIsEditingBank(true)
    }

    const showBankEdit = canViewBank && canEditBank && !!onUpdatePersonal
    // Empty is allowed (clears the field); anything typed must pass the ISO checks.
    const ibanOk = !bankDraft.iban?.trim() || isValidIban(bankDraft.iban)
    const swiftOk = !bankDraft.swift?.trim() || isValidSwift(bankDraft.swift)

    return (
        <Section>
            <SectionHeader
                icon={Coins}
                title={ph.profile.compensationBonusesSection(lang)}
                actions={
                    <>
                        {showCompensation && (compensationData || compensationLineItems) && (
                            <Button
                                variant="tertiary"
                                size="small"
                                iconLeft={salaryRevealed ? EyeOff : Eye}
                                text={
                                    salaryRevealed
                                        ? ph.compensation.hideSalary(lang)
                                        : ph.compensation.revealSalary(lang)
                                }
                                onClick={() => {
                                    // Audit only on reveal; re-hiding is not logged.
                                    if (salaryRevealed) {
                                        setSalaryRevealed(false)
                                        return
                                    }
                                    setSalaryRevealed(true)
                                    if (onRevealCompensation) void onRevealCompensation()
                                }}
                            />
                        )}
                        {showBankEdit && !isEditingBank && (
                            <Button
                                variant="secondary"
                                size="small"
                                text={ph.profile.editProfile(lang)}
                                onClick={openBankEdit}
                            />
                        )}
                    </>
                }
            />

            {showCompensation && (
                <div
                    className={classNames(
                        'portal-profile__comp-wrap',
                        !salaryRevealed && 'portal-profile__comp-wrap--masked',
                    )}
                >
                    {/* Block heading matches the Lisatasud block below (same element/class) */}
                    <h3 className="portal-profile__section-title">
                        {ph.compensation.salariesTitle(lang)}
                    </h3>
                    {compensationData ? (
                        <>
                            {/* Current Compensation Card */}
                            {compensationData.current && (
                                <div className="portal-profile__comp-card">
                                    <div className="portal-profile__comp-card-header">
                                        <span className="portal-profile__comp-card-amount">
                                            {compensationData.current.currency}{' '}
                                            {formatNumber(
                                                compensationData.current.actualAmount,
                                                lang,
                                            )}
                                        </span>
                                        <span className="portal-profile__comp-card-period">
                                            /{' '}
                                            {ph.compensation[compensationData.current.payPeriod](
                                                lang,
                                            )}
                                        </span>
                                        <span className="portal-timeline__badge portal-timeline__badge--current">
                                            {ph.compensation.current(lang)}
                                        </span>
                                    </div>
                                    <div className="portal-profile__comp-details">
                                        <div className="portal-profile__comp-detail">
                                            <span className="portal-profile__field-label">
                                                {ph.compensation.fte(lang)}
                                            </span>
                                            <span className="portal-profile__field-value">
                                                {compensationData.current.fte}
                                            </span>
                                        </div>
                                        <div className="portal-profile__comp-detail">
                                            <span className="portal-profile__field-label">
                                                {ph.compensation.normalizedAmount(lang)}
                                            </span>
                                            <span className="portal-profile__field-value portal-profile__comp-money">
                                                {compensationData.current.currency}{' '}
                                                {formatNumber(
                                                    compensationData.current.grossAmount,
                                                    lang,
                                                )}
                                            </span>
                                        </div>
                                        <div className="portal-profile__comp-detail">
                                            <span className="portal-profile__field-label">
                                                {ph.compensation.effectiveFrom(lang)}
                                            </span>
                                            <span className="portal-profile__field-value">
                                                {formatDisplayDate(
                                                    compensationData.current.effectiveFrom,
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Compensation History Timeline */}
                            {compensationData.history.length > 0 && (
                                <div className="portal-profile__comp-history">
                                    <h4 className="portal-profile__subsection-title">
                                        {ph.compensation.history(lang)}
                                    </h4>
                                    <PortalTimeline
                                        entries={compensationData.history.map(
                                            (rec): TimelineEntry => ({
                                                id: rec.id,
                                                label: `${rec.currency} ${formatNumber(rec.actualAmount, lang)}`,
                                                sublabel: rec.note ?? undefined,
                                                effectiveFrom: rec.effectiveFrom,
                                                effectiveTo: rec.effectiveTo,
                                                isCurrent: rec.isCurrent,
                                                isFuture: rec.isFuture,
                                                badgeVariant: rec.isFuture
                                                    ? 'future'
                                                    : rec.isCurrent
                                                      ? 'current'
                                                      : 'past',
                                                badge: rec.isCurrent
                                                    ? ph.compensation.current(lang)
                                                    : rec.isFuture
                                                      ? ph.compensation.future(lang)
                                                      : undefined,
                                                actions: [
                                                    ...(onUpdateCompensation
                                                        ? [
                                                              {
                                                                  label: ph.compensation.editAction(
                                                                      lang,
                                                                  ),
                                                                  onAction: () => openCompEdit(rec),
                                                              },
                                                          ]
                                                        : []),
                                                    ...(onDeleteCompensation
                                                        ? [
                                                              {
                                                                  label: ph.compensation.deleteAction(
                                                                      lang,
                                                                  ),
                                                                  onAction: () => {
                                                                      requestConfirm(
                                                                          ph.compensation.confirmDelete(
                                                                              lang,
                                                                          ),
                                                                          () =>
                                                                              void onDeleteCompensation(
                                                                                  rec.id,
                                                                              ),
                                                                          {
                                                                              confirmLabel:
                                                                                  ph.compensation.deleteAction(
                                                                                      lang,
                                                                                  ),
                                                                              variant: 'danger',
                                                                          },
                                                                      )
                                                                  },
                                                              },
                                                          ]
                                                        : []),
                                                ],
                                            }),
                                        )}
                                        emptyMessage={ph.compensation.noData(lang)}
                                        lang={lang}
                                    />
                                </div>
                            )}

                            {/* Inline Compensation Form */}
                            {showCompForm ? (
                                <div className="portal-profile__inline-form">
                                    <div className="portal-profile__inline-form-grid">
                                        <div className="portal-profile__inline-form-field">
                                            <TextField
                                                label={ph.compensation.grossSalary(lang)}
                                                inputProps={{
                                                    type: 'number',
                                                    min: '0',
                                                    step: '0.01',
                                                }}
                                                value={compGross}
                                                onChange={(value) => setCompGross(value)}
                                                placeholder="3000"
                                            />
                                        </div>
                                        <div className="portal-profile__inline-form-field">
                                            <Select
                                                label={ph.compensation.currency(lang)}
                                                value={compCurrency}
                                                onChange={(value) => setCompCurrency(value)}
                                                options={[
                                                    { value: 'EUR', label: 'EUR' },
                                                    { value: 'USD', label: 'USD' },
                                                    { value: 'GBP', label: 'GBP' },
                                                ]}
                                            />
                                        </div>
                                        <div className="portal-profile__inline-form-field">
                                            <Select
                                                label={ph.compensation.payPeriod(lang)}
                                                value={compPeriod}
                                                onChange={(value) => setCompPeriod(value)}
                                                options={[
                                                    {
                                                        value: 'monthly',
                                                        label: ph.compensation.monthly(lang),
                                                    },
                                                    {
                                                        value: 'yearly',
                                                        label: ph.compensation.yearly(lang),
                                                    },
                                                    {
                                                        value: 'hourly',
                                                        label: ph.compensation.hourly(lang),
                                                    },
                                                ]}
                                            />
                                        </div>
                                        <div className="portal-profile__inline-form-field">
                                            <TextField
                                                label={ph.compensation.fte(lang)}
                                                inputProps={{
                                                    type: 'number',
                                                    min: '0.1',
                                                    max: '1',
                                                    // 3 decimals: FTE like 0.625 (25h/40h) must round-trip.
                                                    step: '0.001',
                                                }}
                                                value={compFte}
                                                onChange={(value) => setCompFte(value)}
                                            />
                                        </div>
                                        <div className="portal-profile__inline-form-field">
                                            <TextField
                                                label={ph.compensation.effectiveFrom(lang)}
                                                inputProps={{ type: 'date' }}
                                                value={compEffective}
                                                onChange={(value) => setCompEffective(value)}
                                            />
                                        </div>
                                        {editingCompId !== null && (
                                            <div className="portal-profile__inline-form-field">
                                                <TextField
                                                    label={ph.compensation.effectiveTo(lang)}
                                                    inputProps={{ type: 'date' }}
                                                    value={compEffectiveTo}
                                                    onChange={(value) => setCompEffectiveTo(value)}
                                                />
                                            </div>
                                        )}
                                        <div className="portal-profile__inline-form-field portal-profile__inline-form-field--full">
                                            <TextField
                                                label={ph.compensation.note(lang)}
                                                value={compNote}
                                                onChange={(value) => setCompNote(value)}
                                                placeholder={ph.compensation.notePlaceholder(lang)}
                                            />
                                        </div>
                                    </div>
                                    <div className="portal-profile__inline-form-actions">
                                        <Button
                                            text={
                                                compSubmitting
                                                    ? ph.compensation.saving(lang)
                                                    : ph.compensation.saveChange(lang)
                                            }
                                            size="small"
                                            isDisabled={
                                                compSubmitting || !compGross || !compEffective
                                            }
                                            onClick={() => {
                                                const save =
                                                    editingCompId !== null
                                                        ? onUpdateCompensation?.(editingCompId, {
                                                              gross_amount: Number(compGross),
                                                              currency: compCurrency,
                                                              pay_period: compPeriod,
                                                              fte: Number(compFte),
                                                              effective_from: compEffective,
                                                              effective_to: compEffectiveTo || null,
                                                              note: compNote,
                                                          })
                                                        : onCreateCompensation?.({
                                                              gross_amount: Number(compGross),
                                                              currency: compCurrency,
                                                              pay_period: compPeriod,
                                                              fte: Number(compFte),
                                                              effective_from: compEffective,
                                                              note: compNote,
                                                          })
                                                if (!save) return
                                                setCompSubmitting(true)
                                                void save.then((ok) => {
                                                    setCompSubmitting(false)
                                                    if (ok) closeCompForm()
                                                })
                                            }}
                                        />
                                        <Button
                                            text={ph.compensation.cancelAction(lang)}
                                            variant="secondary"
                                            size="small"
                                            onClick={closeCompForm}
                                        />
                                    </div>
                                </div>
                            ) : (
                                onCreateCompensation && (
                                    <Button
                                        variant="tertiary"
                                        size="small"
                                        iconLeft={Plus}
                                        text={ph.compensation.addChange(lang)}
                                        onClick={openCompCreate}
                                    />
                                )
                            )}
                        </>
                    ) : (
                        <div className="portal-profile__empty-state">
                            {ph.compensation.noData(lang)}
                        </div>
                    )}

                    {/* Additional compensation (line items) — admin-gated; null for non-admins */}
                    {compensationLineItems && (
                        <CompensationLineItemsSection
                            groups={compensationLineItems}
                            lang={lang}
                            canManage={Boolean(
                                onCreateLineItem && onUpdateLineItem && onDeleteLineItem,
                            )}
                            types={compensationTypes ?? []}
                            onCreate={onCreateLineItem ?? (() => Promise.resolve(false))}
                            onUpdate={onUpdateLineItem ?? (() => Promise.resolve(false))}
                            onDelete={onDeleteLineItem ?? (() => Promise.resolve(false))}
                            requestConfirm={requestConfirm}
                        />
                    )}
                </div>
            )}

            {/* Pangaandmed — private-tier (self/admin per field permissions) */}
            {canViewBank && (
                <div className="portal-profile__comp-history">
                    <h4 className="portal-profile__subsection-title">
                        {ph.profile.bankDetailsSection(lang)}
                    </h4>
                    {isEditingBank ? (
                        <div className="portal-profile__edit-form">
                            {BANK_FIELDS.map((field) => (
                                <div key={field.key} className="portal-profile__edit-field">
                                    <label className="portal-profile__field-label">
                                        {bankLabels[field.key]}
                                    </label>
                                    <TextField
                                        inputProps={{ maxLength: field.maxLength }}
                                        value={bankDraft[field.key] ?? ''}
                                        onChange={(value) =>
                                            setBankDraft((prev) => ({
                                                ...prev,
                                                [field.key]: value,
                                            }))
                                        }
                                        note={
                                            field.key === 'iban' && !ibanOk
                                                ? {
                                                      text: ph.profile.ibanInvalid(lang),
                                                      isError: true,
                                                  }
                                                : field.key === 'swift' && !swiftOk
                                                  ? {
                                                        text: ph.profile.swiftInvalid(lang),
                                                        isError: true,
                                                    }
                                                  : undefined
                                        }
                                    />
                                </div>
                            ))}
                            <div className="portal-profile__edit-actions">
                                <Button
                                    text={
                                        bankSaving
                                            ? ph.profile.savingProfile(lang)
                                            : ph.profile.saveChanges(lang)
                                    }
                                    size="small"
                                    isDisabled={bankSaving || !ibanOk || !swiftOk}
                                    onClick={() => {
                                        if (!onUpdatePersonal) return
                                        setBankSaving(true)
                                        void onUpdatePersonal({
                                            ...bankDraft,
                                            iban: bankDraft.iban
                                                ? normalizeIban(bankDraft.iban)
                                                : '',
                                            swift: bankDraft.swift
                                                ? normalizeSwift(bankDraft.swift)
                                                : '',
                                        }).then((ok) => {
                                            setBankSaving(false)
                                            if (ok) setIsEditingBank(false)
                                        })
                                    }}
                                />
                                <Button
                                    text={t.common.cancel(lang)}
                                    variant="secondary"
                                    size="small"
                                    onClick={() => setIsEditingBank(false)}
                                />
                            </div>
                        </div>
                    ) : (
                        <>
                            <FieldRow label={ph.profile.bankName(lang)}>
                                {employee.bank_name ?? '—'}
                            </FieldRow>
                            <FieldRow label={ph.profile.bankAccountHolder(lang)}>
                                {employee.bank_account_holder ?? '—'}
                            </FieldRow>
                            <FieldRow label={ph.profile.iban(lang)} monospace>
                                {employee.iban ? formatIban(employee.iban) : '—'}
                                {employee.iban && (
                                    <CopyButton
                                        value={normalizeIban(employee.iban)}
                                        label={ph.profile.copyIban(lang)}
                                        copiedLabel={t.common.copied(lang)}
                                    />
                                )}
                            </FieldRow>
                            <FieldRow label={ph.profile.swift(lang)}>
                                {employee.swift ?? '—'}
                            </FieldRow>
                        </>
                    )}
                </div>
            )}
        </Section>
    )
}
